"use client";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomContext } from "../../CustomComponents/CustomComponents";
import CK_SchemasDS from "../../../DataServices/CK_SchemasDS";
import { insertLog } from "../../Logs/InsertLog";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

export default function useAddSchemaLogic(initialSchema = null) {
  const { hud, stopHudRotation, showAlert, showToast } = useCustomContext();
  const dsApi = new CK_SchemasDS();
  const navigate = useNavigate();
  const [isDatasetOpen, setIsDatasetOpen] = useState(false);
  const [hoveredDataset, setHoveredDataset] = useState(null);
  // USER INFORMATION (for logging)
  const useremail = localStorage.getItem("email");
  const userName = useremail?.split("@")[0];

  // EDIT MODE STATE Track if we're creating a new schema or editing an existing schema
  const [isEditMode, setIsEditMode] = useState(false);
  const [schemaId, setSchemaId] = useState(null);
  const [copyObject, setCopyObject] = useState({});

  // Tracks if user made any changes (used for navigation warnings)
  const [isDirty, setIsDirty] = useState(false);

  // Tracks if JSON was successfully saved to server
  const [isJSONSaved, setIsJSONSaved] = useState(false);

  //MODAL VISIBILITY STATE - Controls which modals are open/closed
  const [openAddModel, setOpenAddModel] = useState(false);
  const [openAddRuleModal, setOpenAddRuleModal] = useState(false);
  const [openJSONModal, setOpenJSONModal] = useState(false);

  // Data Top-level fields (root)
  const [mainFieldsList, setMainFieldsList] = useState([]);

  // Nested fields organized by parent path (key: "0-1-2", value: array of fields)
  const [nestedFieldsMap, setNestedFieldsMap] = useState({});

  // Validation rules organized by field path (key: "0-1", value: array of rules)
  const [rulesMap, setRulesMap] = useState({});

  // Tracks which field/rule is being edited and where to add new fields/rules
  const [currentParentPath, setCurrentParentPath] = useState(null);
  const [editingFieldPath, setEditingFieldPath] = useState(null);
  const [currentRulePath, setCurrentRulePath] = useState(null);
  const [editingRuleIndex, setEditingRuleIndex] = useState(null);

  // Toggles for Controls visibility of nested sections and rule lists
  const [isMainListOpen, setIsMainListOpen] = useState(true);
  const [openNestedLists, setOpenNestedLists] = useState({});
  const [openRulesLists, setOpenRulesLists] = useState({});

  // Metadata for schemas
  const [datasetIdList, setDatasetIdList] = useState([]);
  const [schemaFormData, setSchemaFormData] = useState({
    schema_name: "",
    label: "",
    purpose: "",
    version: "1",
    dataset_id: "",
    enabled: true
  });

  //Json Generation and its editing state
  const [jsonSchema, setJSONSchema] = useState({});
  const [isJSONEditMode, setIsJSONEditMode] = useState(false);
  const [jsonText, setJsonText] = useState("");

  //Validation Error
  const [schemaErrors, setSchemaErrors] = useState({});

  // Called whenever user makes any change to trigger unsaved warnings
  const markDirty = () => {
    if (!isDirty) setIsDirty(true);
  };

  // Safely trims strings, returns empty string for non-strings
  const safeTrim = (val) => typeof val === "string" ? val.trim() : "";

  // Trims and collapses multiple spaces into single space
  const normalizeValue = (value) => typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";

  const normalizeEnabled = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["false", "0", "no", "disabled"].includes(normalized)) return false;
      if (["true", "1", "yes", "enabled"].includes(normalized)) return true;
    }
    if (value === undefined || value === null) return true;
    return Boolean(value);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".custom-select-AS")) {
        setIsDatasetOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  // Use isDirty instead of isJSONSaved for navigation warnings when user unsave
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isJSONSaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    // Sidebar / anchor navigation interception
    const handleSidebarNavigation = (e) => {
      if (!isDirty || isJSONSaved) return;

      const anchor = e.target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");

      // Ignore external links
      if (!href || href.startsWith("http") || href.startsWith("mailto:")) return;

      e.preventDefault();
      e.stopPropagation();

      showAlert(
        "You have unsaved changes. Are you sure you want to leave this page?",
        [
          {
            label: "Stay",
            color: "#999",
            onClick: () => { },
          },
          {
            label: "Leave",
            color: "var(--buttonColor)",
            onClick: () => {
              setIsDirty(false);
              setIsJSONSaved(true);
              navigate(href);
            },
          },
        ]
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleSidebarNavigation, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleSidebarNavigation, true);
    };
  }, [isDirty, isJSONSaved, navigate, showAlert]);

  // Back navigation with unsaved changes warning
  const handleBackNavigation = () => {
    if (isDirty && !isJSONSaved) {
      showAlert(
        "You have unsaved changes. Are you sure you want to go back?",
        [
          {
            label: "No",
            color: "#999",
            onClick: () => { }
          },
          {
            label: "Yes",
            color: "var(--buttonColor)",
            onClick: () => {
              setIsDirty(false);
              navigate(-1);
            }
          }
        ]
      );
    } else {
      navigate(-1);
    }
  };

  // API CALL Fetch dataset ids
  const fetchDatasetIds = async () => {
    try {
      const req = { limit: 100, offset: 0 };
      const response = await dsApi.AP_Dataset_List_GET(req);

      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 && Array.isArray(body.records)) {
        setDatasetIdList(body.records);
      } else {
        insertLog(
          `${userName} failed to load Datasets`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'AP_Dataset_List_GET',
          'FAILED',
          'Unable to load Datasets'
        );
        showAlert(body?.message || "Failed to load Datasets.", [
          { label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }
        ]);
      }
    } catch (err) {
      insertLog(
        `${userName} failed to fetch dataset list`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'AP_Dataset_List_GET',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Something went wrong while fetching Datasets.", [
        { label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }
      ]);
    }
  };

  useEffect(() => {
    fetchDatasetIds();
  }, []);

  // Convert between path arrays and string keys ,path=index ,[0, 1, 2] <-> "0-1-2"
  function pathToKey(path) {
    if (Array.isArray(path)) return path.join("-");
    if (path === null || path === undefined) return null;
    return String(path);
  }

  function parentKeyAndIndexFromPath(path) {
    const arr = Array.isArray(path) ? path.slice() : String(path).split("-").filter(Boolean);
    if (arr.length === 0) return { parentKey: null, index: null };
    const idx = parseInt(arr[arr.length - 1], 10);
    if (arr.length === 1) return { parentKey: null, index: idx };
    const parent = arr.slice(0, -1).join("-");
    return { parentKey: parent, index: idx };
  }
  const getExistingFieldForEdit = () => {
    if (!editingFieldPath) return null;
    const parts = editingFieldPath.split("-").map((p) => parseInt(p, 10));
    if (parts.length === 1) return mainFieldsList[parts[0]] ?? null;
    const parentKey = parts.slice(0, -1).join("-");
    const idx = parts[parts.length - 1];
    return (nestedFieldsMap[parentKey] && nestedFieldsMap[parentKey][idx]) || null;
  };
  // Checks whether two JSON values are exactly the same before saving
  const deepEqual = (a, b) => {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;

    if (typeof a === "object" && a !== null && b !== null) {
      if (Array.isArray(a) !== Array.isArray(b)) return false;

      if (Array.isArray(a)) {
        if (a.length !== b.length) return false;
        return a.every((item, index) => deepEqual(item, b[index]));
      }

      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => deepEqual(a[key], b[key]));
    }

    return false;
  };

  // Handles schema form updates and clears field errors
  const handleSchemaFormChange = (e) => {
    const { name, type, value, checked } = e.target;

    if (type === "checkbox") {
      setSchemaFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));

      setSchemaErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));

      markDirty();
      return;
    }

    if (name === "schema_name") {

      const limitedValue = value.slice(0, 50);
    
      if (value.length > 50) {
        setSchemaErrors((prev) => ({
          ...prev,
          schema_name: "Schema Name cannot exceed 50 characters"
        }));
      } else {
        setSchemaErrors((prev) => ({
          ...prev,
          schema_name: ""
        }));
      }
    
      setSchemaFormData((prev) => ({
        ...prev,
        schema_name: limitedValue,
      }));
    
      markDirty();
      return;
    }

    setSchemaFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setSchemaErrors((prev) => ({
      ...prev,
      [name]: safeTrim(value) ? undefined : prev[name],
    }));

    markDirty();
  };


  //Toggle to Controls accordion open/close state
  const toggleMainList = () => setIsMainListOpen((p) => !p);

  const toggleNestedList = (path) => {
    const key = pathToKey(path);
    setOpenNestedLists((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleRulesList = (path) => {
    const key = pathToKey(path);
    setOpenRulesLists((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  //Opens and closes the Add/Edit Field modal and sets the active path
  const handleOpenAddModel = (parentPath = null, editPath = null) => {
    setCurrentParentPath(parentPath !== undefined ? pathToKey(parentPath) : null);
    setEditingFieldPath(editPath !== undefined && editPath !== null ? pathToKey(editPath) : null);
    setOpenAddModel(true);
  };

  const handleCloseAddModel = () => {
    setOpenAddModel(false);
    setCurrentParentPath(null);
    setEditingFieldPath(null);
  };

  // Saves new field or updates existing field based on editingFieldPath
  const handleSaveMainField = (fieldObj) => {
    markDirty();
    setIsJSONSaved(false);

    if (editingFieldPath) {
      const { parentKey, index } = parentKeyAndIndexFromPath(editingFieldPath);
      if (parentKey === null) {
        setMainFieldsList((prev) => {
          const copy = [...prev];
          copy[index] = fieldObj;
          return copy;
        });
      } else {
        setNestedFieldsMap((prev) => {
          const copy = { ...prev };
          const list = copy[parentKey] ? [...copy[parentKey]] : [];
          list[index] = fieldObj;
          copy[parentKey] = list;
          return copy;
        });
      }
      setEditingFieldPath(null);
      setOpenAddModel(false);
      return;
    }
    if (!currentParentPath) {
      setMainFieldsList((prev) => [...prev, fieldObj]);
      setIsMainListOpen(true);
    } else {
      setNestedFieldsMap((prev) => {
        const key = currentParentPath;
        const existing = prev[key] ? [...prev[key]] : [];
        return { ...prev, [key]: [...existing, fieldObj] };
      });
      setOpenNestedLists((prev) => ({ ...prev, [currentParentPath]: true }));
    }
    setCurrentParentPath(null);
    setOpenAddModel(false);
  };

  // Opens modal with existing field data for editing
  const handleEditMainField = (path) => {
    const key = pathToKey(path);
    setEditingFieldPath(key);
    setCurrentParentPath(null);
    setOpenAddModel(true);
  };

  // Deletes field and reindexes all child paths
  const handleDeleteMainField = (deleteIndex) => {
    markDirty();
    setIsJSONSaved(false);

    setMainFieldsList(prev => {
      const updated = prev.filter((_, i) => i !== deleteIndex);

      setNestedFieldsMap(prevNested => {
        const newMap = {};

        Object.entries(prevNested).forEach(([key, value]) => {
          const [root, ...rest] = key.split("-").map(Number);

          if (root === deleteIndex) return;
          if (root > deleteIndex) {
            newMap[[root - 1, ...rest].join("-")] = value;
          } else {
            newMap[key] = value;
          }
        });

        return newMap;
      });

      // Reindex rules map
      setRulesMap(prevRules => {
        const newMap = {};

        Object.entries(prevRules).forEach(([key, value]) => {
          const [root, ...rest] = key.split("-").map(Number);

          if (root === deleteIndex) return;
          if (root > deleteIndex) {
            newMap[[root - 1, ...rest].join("-")] = value;
          } else {
            newMap[key] = value;
          }
        });

        return newMap;
      });

      return updated;
    });
  };

  // Opens modal to add a child field to group/list type field
  const handleAddNestedField = (parentPath) => {
    markDirty();
    setIsJSONSaved(false);

    const key = pathToKey(parentPath);
    setCurrentParentPath(key);
    setEditingFieldPath(null);
    setOpenAddModel(true);
  };

  // Deletes a root or nested field with confirmation and reindexes remaining fields
  const handleDeleteNestedField = (path) => {
    const key = pathToKey(path);
    const { parentKey, index } = parentKeyAndIndexFromPath(key);
    //  Case 1: Root-level field → delegate to main delete (already has alert)
    if (parentKey === null) {
      showAlert(
        "Are you sure you want to delete this field?",
        [
          {
            label: "Cancel",
            color: "#999",
            onClick: () => { }
          },
          {
            label: "Ok",
            color: "var(--buttonColor)",
            onClick: () => handleDeleteMainField(index)
          }
        ]
      );
      return;
    }

    // Case 2: Child-level nested field → ADD missing alert here 
    showAlert(
      "Are you sure you want to delete this nested field?",
      [
        {
          label: "Cancel",
          color: "#999",
          onClick: () => { }
        },
        {
          label: "Ok",
          color: "var(--buttonColor)",
          onClick: () => {
            markDirty();
            setIsJSONSaved(false);

            // ---- Update nested fields ----
            setNestedFieldsMap((prev) => {
              const newMap = {};
              const parentArr = [...(prev[parentKey] || [])];
              parentArr.splice(index, 1);

              Object.entries(prev).forEach(([k, v]) => {
                if (k === parentKey) return;
                if (k === key || k.startsWith(`${key}-`)) return;

                // Reindex siblings
                if (k.startsWith(`${parentKey}-`)) {
                  const parts = k.split("-").map(Number);
                  if (parts[parts.length - 1] > index) {
                    parts[parts.length - 1]--;
                  }
                  newMap[parts.join("-")] = v;
                } else {
                  newMap[k] = v;
                }
              });

              newMap[parentKey] = parentArr;
              return newMap;
            });

            // ---- Update rules ----
            setRulesMap((prev) => {
              const newMap = {};

              Object.entries(prev).forEach(([k, v]) => {
                // Remove deleted node & its children rules
                if (k === key || k.startsWith(`${key}-`)) return;

                // Reindex sibling rules
                if (k.startsWith(`${parentKey}-`)) {
                  const parts = k.split("-").map(Number);
                  if (parts[parts.length - 1] > index) {
                    parts[parts.length - 1]--;
                  }
                  newMap[parts.join("-")] = v;
                } else {
                  newMap[k] = v;
                }
              });

              return newMap;
            });
          }
        }
      ]
    );
  };

  // MODAL HANDLERS: Add/Edit Rule
  const handleOpenAddRuleModal = (fieldPath, ruleIndex = null) => {
    markDirty();
    setIsJSONSaved(false);

    const key = pathToKey(fieldPath);
    setCurrentRulePath(key);
    setEditingRuleIndex(ruleIndex);
    setOpenAddRuleModal(true);
  };

  const handleCloseRulesModal = () => {
    setOpenAddRuleModal(false);
    setCurrentRulePath(null);
    setEditingRuleIndex(null);
  };

  // Validates no duplicate rule types, then saves or updates rule
  const handleSaveRule = (ruleObj) => {
    markDirty();
    setIsJSONSaved(false);

    if (!currentRulePath) {
      console.warn("No rule path set");
      setOpenAddRuleModal(false);
      return;
    }

    const existingRules = rulesMap[currentRulePath] || [];

    const duplicateIndex = existingRules.findIndex(
      (r, idx) =>
        r.type === ruleObj.type &&
        idx !== editingRuleIndex
    );

    if (duplicateIndex !== -1) {
      showAlert(
        `Rule type "${ruleObj.type}" already exists for this field. Each field can have only one rule per rule type.`,
        [
          {
            label: "Ok",
            color: "var(--buttonColor)",
            onClick: () => { }
          }
        ]
      );
      return;
    }

    if (editingRuleIndex !== null) {
      setRulesMap((prev) => {
        const copy = { ...prev };
        const arr = [...(copy[currentRulePath] || [])];
        arr[editingRuleIndex] = ruleObj;
        copy[currentRulePath] = arr;
        return copy;
      });
    } else {
      setRulesMap((prev) => ({
        ...prev,
        [currentRulePath]: [...existingRules, ruleObj]
      }));
    }

    setOpenRulesLists((prev) => ({
      ...prev,
      [currentRulePath]: true
    }));

    setOpenAddRuleModal(false);
    setCurrentRulePath(null);
    setEditingRuleIndex(null);
  };

  //Deletes a validation rule after confirmation
  const handleDeleteRule = (fieldPath, ruleIndex) => {
    const key = pathToKey(fieldPath);
    showAlert(
      "Are you sure you want to delete this rule?",
      [{
        label: "Cancel",
        color: "#999",
        onClick: () => { }
      },
      {
        label: "Ok",
        color: "var(--buttonColor)",
        onClick: () => {
          markDirty();
          setIsJSONSaved(false);

          setRulesMap((prev) => {
            const copy = { ...prev };
            const arr = copy[key] ? [...copy[key]] : [];
            arr.splice(ruleIndex, 1);
            copy[key] = arr;
            return copy;
          });
        },
      },

      ]
    );
  };

  // Gets the selected rule data for editing
  const getExistingRuleForEdit = () => {
    if (!currentRulePath || editingRuleIndex === null) return null;
    const rules = rulesMap[currentRulePath];
    return rules && rules[editingRuleIndex] ? rules[editingRuleIndex] : null;
  };

  // Recursively builds API-ready JSON for fields
  const buildFieldObject = (field, pathKey) => {
    const obj = {
      field_name: field.field_name,
      data_type: field.data_type,
      ui_type: field.ui_type,
      label: field.label,
      visibility: field.visibility,
      source: field.source,
      help_text: field.help_text,
      ck_dictionary_id: field.ck_dictionary_id || "",
      value_separator: field.value_separator || "",
      options: field.options || [],
      rules: rulesMap[pathKey] || []
    };
    const childrenList = nestedFieldsMap[pathKey];
    if (childrenList && childrenList.length > 0) {
      obj.fields = childrenList.map((child, idx) => buildFieldObject(child, `${pathKey}-${idx}`));
    }

    return obj;
  };

  //Validates data, generates schema JSON, and opens preview
  const handleGenerateJSON = () => {
    const errors = validateSchemaForm(schemaFormData);
    setSchemaErrors(errors);

    if (Object.keys(errors).length > 0) return;

    const fields = mainFieldsList.map((f, idx) =>
      buildFieldObject(f, `${idx}`)
    );

    const generated = {
      schema_name: schemaFormData.schema_name,
      label: schemaFormData.label,
      version: schemaFormData.version,
      purpose: schemaFormData.purpose,
      dataset_id: schemaFormData.dataset_id,
      enabled: normalizeEnabled(schemaFormData.enabled),
      fields,
    };
    const { _id, ...cleanJson } = generated;
    setJSONSchema(cleanJson);
    setJsonText(JSON.stringify(cleanJson, null, 2));
    setIsJSONEditMode(false);
    setOpenJSONModal(true);
  };

  //Enables JSON editing, validates changes, or restores original JSON
  const handleEnableJSONEdit = () => {
    setIsJSONEditMode(true);
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonText(value);
    try {
      const parsed = JSON.parse(value);
      setJSONSchema(parsed);
      setSchemaFormData(parsed);
      setSchemaErrors({});
    } catch {
      setSchemaErrors({ json: "Invalid JSON format" });
    }
  };

  const handleCancelJSONEdit = () => {
    const isSame = deepEqual(copyObject, jsonSchema);

    if (!isSame) {
      showAlert(
        "You have unsaved changes. Do you want to cancel?",
        [
          {
            label: "Yes",
            color: "var(--buttonColor)",
            onClick: () => {
              setJSONSchema(copyObject);
              setJsonText(JSON.stringify(copyObject, null, 2));
              setIsJSONEditMode(false);
              setSchemaErrors({});
            }
          },
          {
            label: "No",
            color: "#999",
            onClick: () => { }
          }
        ]
      );
      return;
    }

    // If nothing changed
    setJSONSchema(copyObject);
    setJsonText(JSON.stringify(copyObject, null, 2));
    setIsJSONEditMode(false);
    setSchemaErrors({});
  };

  // validate the schema and call either INSERT or UPDATE API
  const handleFinalSave = async (finalJson) => {
    const normalizedFinalJson = {
      ...finalJson,
      schema_name: normalizeValue(finalJson.schema_name),
      label: normalizeValue(finalJson.label),
      version: normalizeValue(finalJson.version),
      purpose: normalizeValue(finalJson.purpose),
      dataset_id: typeof finalJson.dataset_id === "string"
        ? normalizeValue(finalJson.dataset_id)
        : finalJson.dataset_id,
      enabled: normalizeEnabled(finalJson.enabled),
    };
    if (
      !safeTrim(normalizedFinalJson.schema_name) ||
      !safeTrim(normalizedFinalJson.label) ||
      !safeTrim(normalizedFinalJson.version)
    ) {
      showAlert(
        "Please include the Schema Name, Label, and Version before saving the schema.",
        [
          {
            label: "Ok",
            color: "var(--buttonColor)",
            onClick: () => { }
          }
        ]
      );
      return;
    }
    if (isEditMode) {
      const copyNormalized = {
        schema_name: normalizeValue(copyObject.schema_name || ""),
        label: normalizeValue(copyObject.label || ""),
        version: normalizeValue(copyObject.version || ""),
        purpose: normalizeValue(copyObject.purpose || ""),
        dataset_id: typeof copyObject.dataset_id === "string"
          ? normalizeValue(copyObject.dataset_id)
          : (copyObject.dataset_id || ""),
        enabled: normalizeEnabled(copyObject.enabled),
        fields: copyObject.fields || [],
      };
      const isSame = deepEqual(copyNormalized, normalizedFinalJson);
      if (isSame) {
        showAlert("No modifications have been identified.", [
          {
            label: "Ok",
            color: "var(--buttonColor)",
            onClick: () => { }
          }
        ]);
        return;
      }
    }
    const errors = validateSchemaForm(normalizedFinalJson);
    setSchemaErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      hud("Please wait...");
      const isDuplicate = await checkDuplicateSchemaName(normalizedFinalJson.schema_name);
      if (isDuplicate) {
        stopHudRotation();
        showAlert(
          `Schema "${normalizedFinalJson.schema_name}" already exists.`,
          [{ label: "Ok", color: "var(--buttonColor)", onClick: () => { } }]
        );
        return;
      }

      const hasDatasetPurposeChanged =
        normalizeValue(copyObject.dataset_id).toLowerCase() !== normalizeValue(normalizedFinalJson.dataset_id).toLowerCase() ||
        normalizeValue(copyObject.purpose).toLowerCase() !== normalizeValue(normalizedFinalJson.purpose).toLowerCase();

      if (!isEditMode || hasDatasetPurposeChanged) {
        const duplicateDatasetPurpose = await checkDuplicateDatasetPurpose(
          normalizedFinalJson.dataset_id,
          normalizedFinalJson.purpose,
          isEditMode ? schemaId : null
        );
        if (duplicateDatasetPurpose) {
          stopHudRotation();
          showAlert(
            `${normalizedFinalJson.dataset_id} cannot ${isEditMode ? "update" : "create"} a schema with this existing ${normalizedFinalJson.purpose} purpose`,
            [{ label: "Ok", color: "var(--buttonColor)", onClick: () => { } }]
          );
          return;
        }
      }

      let response;
      if (isEditMode && schemaId) {
        response = await dsApi.WS_CK_Schema_ByID_UPDATE({
          _id: schemaId,
          ...normalizedFinalJson,
          updated_by_email_id: useremail
        });
      }
      else {
        response = await dsApi.WS_CK_Schema_INSERT({
          ...normalizedFinalJson,
          created_by_email_id: useremail   // ✅ ADD THIS
        });
      }
      const data = response?.data;
      const body =
        typeof data?.body === "string"
          ? JSON.parse(data.body)
          : data?.body;
      stopHudRotation();
      if (data?.statusCode === 200) {
        setIsJSONSaved(true);
        insertLog(
          `${userName} ${isEditMode ? "updated" : "created"} schema ${normalizedFinalJson.schema_name}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          schemaId || '',
          isEditMode ? 'UPDATE' : 'INSERT',
          isEditMode ? 'WS_CK_Schema_ByID_UPDATE' : 'WS_CK_Schema_INSERT',
          'SUCCESS',
          ''
        );
        showToast({
          message: isEditMode
            ? "CK Schema updated successfully"
            : "CK Schema saved successfully"
        });
        setOpenJSONModal(false);
        navigate("/CK_Schemas");
      } else {
        insertLog(
          `${userName} failed to ${isEditMode ? "update" : "create"} schema ${normalizedFinalJson.schema_name}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          schemaId || '',
          isEditMode ? 'UPDATE' : 'INSERT',
          isEditMode ? 'WS_CK_Schema_ByID_UPDATE' : 'WS_CK_Schema_INSERT',
          'FAILED',
          'Failed to save CK Schema'
        );
        showAlert(
          body?.message || "Failed to save CK Schema",
          [{ label: "Ok", color: "var(--buttonColor)", onClick: () => { } }]
        );
      }
    } catch (err) {
      insertLog(
        `${userName} failed to ${isEditMode ? "update" : "create"} schema ${normalizedFinalJson.schema_name}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        schemaId || '',
        isEditMode ? 'UPDATE' : 'INSERT',
        isEditMode ? 'WS_CK_Schema_ByID_UPDATE' : 'WS_CK_Schema_INSERT',
        'FAILED',
        err.message
      );
      stopHudRotation()
      showAlert(err.message || "Error saving CK Schema!", [
        { label: "Ok", color: "var(--buttonColor)", onClick: () => { } }
      ]);
    }
  };

  // checks if the edited JSON has changes and then calls handleFinalSave
  const handleSaveJSONEdit = async () => {
    const copyNormalized = {
      schema_name: normalizeValue(copyObject.schema_name || ""),
      label: normalizeValue(copyObject.label || ""),
      version: normalizeValue(copyObject.version || ""),
      purpose: normalizeValue(copyObject.purpose || ""),
      dataset_id: typeof copyObject.dataset_id === "string"
        ? normalizeValue(copyObject.dataset_id)
        : (copyObject.dataset_id || ""),
      enabled: normalizeEnabled(copyObject.enabled),
      fields: copyObject.fields || [],
    };
    const isSame = deepEqual(copyNormalized, jsonSchema);
    if (isSame) {
      showAlert("No modifications have been identified.", [
        {
          label: "Ok",
          color: "var(--buttonColor)",
          onClick: () => { }
        }
      ]);
      return;
    }
    await handleFinalSave(jsonSchema);
  };
  // Ensures required fields are filled
  const validateSchemaForm = (data) => {
    const errors = {};

    if (!safeTrim(data.schema_name))
      errors.schema_name = "Schema Name is required";

    if (!safeTrim(data.label))
      errors.label = "Label is required";

    if (!safeTrim(data.purpose))
      errors.purpose = "Purpose is required";

    return errors;
  };

  // Downloads schema as JSON file
  const downloadJSON = (jsonData) => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${jsonData.schema_name || "schema"}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setIsJSONSaved(true);
  };

  //Alert is shown when json not saved 
  const handleCloseJSONModal = () => {
    if (isJSONEditMode) {
      const isSame = deepEqual(copyObject, jsonSchema);
      if (!isSame) {
        showAlert(
          "You have unsaved changes. Do you want to cancel?",
          [
            {
              label: "Yes",
              color: "var(--buttonColor)",
              onClick: () => {
                setOpenJSONModal(false);
                setIsJSONEditMode(false);
              }
            },
            {
              label: "No",
              color: "#999",
              onClick: () => { }
            }
          ]
        );
        return;
      }
    }
    setOpenJSONModal(false);
  };

  //LOAD incoming schema (for Edit) 
  const processIncomingSchema = useCallback((schemaObj) => {
    if (!schemaObj) return;
    try {
      // Set edit mode if _id exists
      if (schemaObj._id) {
        setIsEditMode(true);
        setSchemaId(schemaObj._id);
        const { _id, ...withoutId } = schemaObj;
        setCopyObject(withoutId);
      } else {
        setIsEditMode(false);
        setSchemaId(null);
        setCopyObject({});
      }
      // Populate form data
      setSchemaFormData({
        schema_name: schemaObj.schema_name || "",
        label: schemaObj.label || "",
        purpose: schemaObj.purpose || "",
        version: schemaObj.version || "",
        dataset_id: schemaObj.dataset_id || "",
        enabled: normalizeEnabled(schemaObj.enabled)
      });
      // Reset field structures
      setMainFieldsList([]);
      setNestedFieldsMap({});
      setRulesMap({});

      const newMain = [];
      const newNested = {};
      const newRules = {};
      // Recursive function to process field tree
      const walk = (field, pathKey) => {
        const normalized = {
          field_name: field.field_name || "",
          data_type: field.data_type || "",
          ui_type: field.ui_type || "",
          label: field.label || "",
          purpose: field.purpose || "",
          visibility: field.visibility || "",
          source: field.source || "",
          help_text: field.help_text || "",
          ck_dictionary_id: field.ck_dictionary_id || "",
          value_separator: field.value_separator || "",
          options: field.options || []
        };
        // Store rules
        if (field.rules && Array.isArray(field.rules) && field.rules.length > 0) {
          newRules[pathKey] = field.rules.map(r => ({ ...r }));
        }
        // Add to appropriate structure
        if (!pathKey.includes("-")) {
          const idx = parseInt(pathKey, 10);
          newMain[idx] = normalized;
        } else {
          // Nested level
          const parentKey = pathKey.split("-").slice(0, -1).join("-");
          const idx = parseInt(pathKey.split("-").slice(-1)[0], 10);
          if (!newNested[parentKey]) newNested[parentKey] = [];
          newNested[parentKey][idx] = normalized;
        }
        // Process children recursively
        const children = field.fields || field.nested_fields || [];
        if (Array.isArray(children) && children.length > 0) {
          children.forEach((child, ci) => {
            const childPath = `${pathKey}-${ci}`;
            walk(child, childPath);
          });
        }
      };

      // Process all top-level fields
      const topFields = schemaObj.fields || [];
      topFields.forEach((f, i) => {
        walk(f, `${i}`);
      });
      // Clean up undefined entries
      const compactedMain = newMain.filter((x) => x !== undefined);
      Object.keys(newNested).forEach((k) => {
        newNested[k] = (newNested[k] || []).filter((x) => x !== undefined);
      });
      // Update state
      setMainFieldsList(compactedMain);
      setNestedFieldsMap(newNested);
      setRulesMap(newRules);
      // Reset UI toggles
      setOpenNestedLists({});
      setOpenRulesLists({});
      // Mark as not dirty since we just loaded
      setIsDirty(false);
      setIsJSONSaved(true);

    } catch (err) {
      console.error("Failed to process incoming schema:", err);
    }
  }, []);

  // Process initial schema on mount
  useEffect(() => {
    if (initialSchema) {
      processIncomingSchema(initialSchema);
    }
  }, [initialSchema, processIncomingSchema]);

  // Used for displaying field names in rule modal dropdowns
  const getFieldNameByPath = useCallback(
    (pathKey) => {
      if (!pathKey) return "";

      const parts = pathKey.split("-").map(Number);
      const rootIndex = parts[0];

      if (parts.length === 1) {
        return mainFieldsList[rootIndex]?.field_name || "";
      }

      const parentKey = parts.slice(0, -1).join("-");
      const childIndex = parts[parts.length - 1];

      return nestedFieldsMap[parentKey]?.[childIndex]?.field_name || "";
    },
    [mainFieldsList, nestedFieldsMap]
  );

  // Get all root field names in compare field
  const getAllFieldNames = useCallback(() => {
    const names = [];

    const currentFieldName = getFieldNameByPath(currentRulePath);

    mainFieldsList.forEach((f) => {
      if (f?.field_name && f.field_name !== currentFieldName) {
        names.push(f.field_name);
      }
    });

    // Get all nested field names
    Object.values(nestedFieldsMap).forEach((list) => {
      list.forEach((f) => {
        if (f?.field_name && f.field_name !== currentFieldName) {
          names.push(f.field_name);
        }
      });
    });

    return names;
  }, [mainFieldsList, nestedFieldsMap, currentRulePath, getFieldNameByPath]);

  // Check duplicate schema name before save
  const checkDuplicateSchemaName = async (schemaName) => {
    try {
      const response = await dsApi.WS_CK_Schemas_List_GET({});
      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 && Array.isArray(body.data)) {
        const duplicate = body.data.find((s) => {
          const isSameName = s.schema_name?.trim().toLowerCase() === schemaName.trim().toLowerCase();
          // In edit mode, exclude the current schema from duplicate check
          if (isEditMode && schemaId) {
            return isSameName && s._id !== schemaId;
          }
          return isSameName;
        });
        return !!duplicate;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Check duplicate dataset + purpose before save
  const checkDuplicateDatasetPurpose = async (datasetId, purpose, excludeSchemaId = null) => {
    const normalizedDatasetId = normalizeValue(datasetId).toLowerCase();
    const normalizedPurpose = normalizeValue(purpose).toLowerCase();

    if (!normalizedDatasetId || !normalizedPurpose) return false;

    try {
      const response = await dsApi.WS_CK_Schemas_List_GET({});
      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 && Array.isArray(body.data)) {
        const duplicate = body.data.find((s) => (
          s._id !== excludeSchemaId &&
          normalizeValue(s.dataset_id).toLowerCase() === normalizedDatasetId &&
          normalizeValue(s.purpose).toLowerCase() === normalizedPurpose
        ));

        return !!duplicate;
      }

      return false;
    } catch {
      return false;
    }
  };

  return {
    // Modal states
    openAddModel,
    openAddRuleModal,
    openJSONModal,

    // Data structures
    mainFieldsList,
    nestedFieldsMap,
    rulesMap,

    // Edit context
    currentParentPath,
    editingFieldPath,
    currentRulePath,
    editingRuleIndex,

    // UI toggles
    isMainListOpen,
    openNestedLists,
    openRulesLists,

    // Metadata
    datasetIdList,
    schemaFormData,
    jsonSchema,

    // Status 
    isJSONSaved,
    isEditMode,
    isDirty,

    // Validation
    schemaErrors,

    // JSON editing
    isJSONEditMode,
    jsonText,
    getExistingFieldForEdit,
    // Utility functions
    getFieldNameByPath,
    getAllFieldNames,
    markDirty,

    // Navigation handlers
    handleBackNavigation,

    // Form handlers
    handleSchemaFormChange,

    // Toggle handlers
    toggleMainList,
    toggleNestedList,
    toggleRulesList,

    // Field modal handlers
    handleOpenAddModel,
    handleCloseAddModel,
    handleSaveMainField,
    handleEditMainField,
    handleDeleteMainField,
    handleAddNestedField,
    handleDeleteNestedField,

    // Rule modal handlers
    handleOpenAddRuleModal,
    handleCloseRulesModal,
    handleSaveRule,
    handleDeleteRule,
    getExistingRuleForEdit,

    // JSON handlers
    handleGenerateJSON,
    handleFinalSave,
    downloadJSON,
    setOpenJSONModal,

    // JSON edit 
    handleEnableJSONEdit,
    handleJsonChange,
    handleCancelJSONEdit,
    handleSaveJSONEdit,
    handleCloseJSONModal,
    isDatasetOpen,
    setIsDatasetOpen,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    hoveredDataset,
    setHoveredDataset,
  };
}
